import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useRegisterSsoClientMutation } from "Api/Mutations/SsoClients/useRegisterSsoClientMutation";
import { useUpdateSsoClientMutation } from "Api/Mutations/SsoClients/useUpdateSsoClientMutation";
import { CheckboxInput } from "Components/Shared/Inputs/Form/CheckboxInput";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { StringListInput } from "Components/Shared/Inputs/Form/StringListInput";
import type { SsoClientDto, SsoClientRequest } from "Infrastructure/Api/Api";
import { hasApiProblemError } from "Infrastructure/Api/ApiUtils";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import type { CodeListItem } from "Models/ICodeListDto";
import {
  getSsoClientSchema,
  type SsoClientFormData,
} from "Schemas/ssoClientSchema";
import { Resources, useResource } from "Translations/Resources";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

const RESOURCES = Resources.Admin.SsoClients;

// Keep in sync with SsoClientDefinitions.RolesAClientMayActAs on BE.
const ROLES_A_CLIENT_MAY_ACT_AS: Array<CodeListItem> = [
  { code: AccessRoleCodes.Merchant, name: AccessRoleCodes.Merchant },
  { code: AccessRoleCodes.Employee, name: AccessRoleCodes.Employee },
  { code: AccessRoleCodes.Disponent, name: AccessRoleCodes.Disponent },
  { code: AccessRoleCodes.Hunter, name: AccessRoleCodes.Hunter },
  { code: AccessRoleCodes.HunterPlus, name: AccessRoleCodes.HunterPlus },
];

// Keep in sync with SsoApiScopes on BE; the names are translated, the codes are what the API knows.
const useApiScopeOptions = (): Array<CodeListItem> => {
  const { t } = useResource();
  return [
    { code: "payments", name: t(RESOURCES.Scopes.Payments) },
    { code: "merchant", name: t(RESOURCES.Scopes.Merchant) },
    { code: "hunter", name: t(RESOURCES.Scopes.Hunter) },
    { code: "terminals:manage", name: t(RESOURCES.Scopes.TerminalsManage) },
    { code: "directory:read", name: t(RESOURCES.Scopes.DirectoryRead) },
    { code: "coupons:create", name: t(RESOURCES.Scopes.CouponsCreate) },
    { code: "loyalty:read", name: t(RESOURCES.Scopes.LoyaltyRead) },
  ];
};

const EMPTY_FORM: SsoClientFormData = {
  clientId: "",
  displayName: "",
  isConfidential: true,
  redirectUris: [],
  postLogoutRedirectUris: [],
  allowedRoles: [],
  allowedScopes: [],
};

const toFormData = (client: SsoClientDto): SsoClientFormData => ({
  clientId: client.clientId,
  displayName: client.displayName,
  isConfidential: client.isConfidential,
  redirectUris: client.redirectUris,
  postLogoutRedirectUris: client.postLogoutRedirectUris,
  allowedRoles: client.allowedRoles,
  allowedScopes: client.allowedScopes,
});

const toRequest = (data: SsoClientFormData): SsoClientRequest => ({
  clientId: data.clientId,
  displayName: data.displayName,
  isConfidential: data.isConfidential,
  redirectUris: data.redirectUris,
  postLogoutRedirectUris: data.postLogoutRedirectUris,
  allowedRoles: data.isConfidential ? data.allowedRoles : [],
  allowedScopes: data.isConfidential ? data.allowedScopes : [],
});

type Props = {
  isOpen: boolean;
  /** Present when editing; the id and the type of an existing client cannot change. */
  client?: SsoClientDto;
  onClose: () => void;
  /** A freshly registered confidential client: the secret is shown once. */
  onSecretIssued: (clientId: string, secret: string) => void;
};

export const SsoClientFormDialog = ({
  isOpen,
  client,
  onClose,
  onSecretIssued,
}: Props) => {
  const { t } = useResource();
  const apiScopeOptions = useApiScopeOptions();
  const isEdit = client !== undefined;

  const { control, handleSubmit, watch, reset } = useForm<SsoClientFormData>({
    resolver: zodResolver(getSsoClientSchema(t)),
    defaultValues: client ? toFormData(client) : EMPTY_FORM,
  });
  const isConfidential = watch("isConfidential");

  const handleClose = () => {
    reset();
    onClose();
  };

  const showSaveError = (error: unknown, fallback: string) => {
    if (hasApiProblemError(error, "ClientId")) {
      toast.error(t(RESOURCES.Messages.ClientIdTaken));
      return;
    }
    toast.error(fallback);
  };

  const { mutate: register, isPending: isRegistering } =
    useRegisterSsoClientMutation({
      onSuccess: (result) => {
        toast.success(t(RESOURCES.Messages.CreateSuccess));
        handleClose();
        if (result.clientSecret) {
          onSecretIssued(result.client.clientId, result.clientSecret);
        }
      },
      onError: (error) =>
        showSaveError(error, t(RESOURCES.Messages.CreateError)),
    });

  const { mutate: update, isPending: isUpdating } = useUpdateSsoClientMutation({
    onSuccess: () => {
      toast.success(t(RESOURCES.Messages.UpdateSuccess));
      handleClose();
    },
    onError: (error) => showSaveError(error, t(RESOURCES.Messages.UpdateError)),
  });

  const onSubmit = (data: SsoClientFormData) => {
    if (client) {
      update({ clientId: client.clientId, data: toRequest(data) });
      return;
    }
    register(toRequest(data));
  };

  const isPending = isRegistering || isUpdating;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {isEdit
            ? t(RESOURCES.Form.EditTitle, { clientId: client.clientId })
            : t(RESOURCES.Form.CreateTitle)}
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <FormInput
              control={control}
              name="clientId"
              label={t(RESOURCES.Form.ClientId)}
              maxLength={64}
              disabled={isEdit}
              valueTransformer={(value) => value.toLowerCase()}
            />
            <FormInput
              control={control}
              name="displayName"
              label={t(RESOURCES.Form.DisplayName)}
              maxLength={100}
            />
            <CheckboxInput
              control={control}
              name="isConfidential"
              label={t(RESOURCES.Form.IsConfidential)}
              disabled={isEdit}
            />
            <Typography variant="caption" color="text.secondary">
              {t(RESOURCES.Form.IsConfidentialHint)}
            </Typography>
            <StringListInput
              control={control}
              name="redirectUris"
              label={t(RESOURCES.Form.RedirectUris)}
              placeholder={t(RESOURCES.Form.ListPlaceholder)}
              helperText={t(RESOURCES.Form.RedirectUrisHint)}
            />
            <StringListInput
              control={control}
              name="postLogoutRedirectUris"
              label={t(RESOURCES.Form.PostLogoutRedirectUris)}
              placeholder={t(RESOURCES.Form.ListPlaceholder)}
            />
            {isConfidential && (
              <>
                <Dropdown
                  control={control}
                  name="allowedRoles"
                  label={t(RESOURCES.Form.AllowedRoles)}
                  accessibilityLabel={t(RESOURCES.Form.AllowedRoles)}
                  codeList={ROLES_A_CLIENT_MAY_ACT_AS}
                  isMultiple
                />
                <Typography variant="caption" color="text.secondary">
                  {t(RESOURCES.Form.AllowedRolesHint)}
                </Typography>
                <Dropdown
                  control={control}
                  name="allowedScopes"
                  label={t(RESOURCES.Form.AllowedScopes)}
                  accessibilityLabel={t(RESOURCES.Form.AllowedScopes)}
                  codeList={apiScopeOptions}
                  isMultiple
                />
                <Typography variant="caption" color="text.secondary">
                  {t(RESOURCES.Form.AllowedScopesHint)}
                </Typography>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t(RESOURCES.Form.Cancel)}</Button>
          <Button type="submit" variant="contained" disabled={isPending}>
            {isEdit ? t(RESOURCES.Form.Save) : t(RESOURCES.Form.Submit)}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
